// src/pages/CreateAlertRulePage.js - Inline Create Alert Rule Page
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { eventService, alertRuleService } from '../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import TriggerIcon from '@mui/icons-material/Settings';
import ActionIcon from '@mui/icons-material/NotificationsActive';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MessageIcon from '@mui/icons-material/Message';
import RepeatIcon from '@mui/icons-material/Repeat';

const CreateAlertRulePage = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const location = useLocation();
  const editingRule = location.state?.editingRule;
  const [event, setEvent] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    triggerField: 'rsvp',
    triggerValue: 'no',
    actionType: 'whatsapp_message',
    isRecurring: false,
    frequency: 'immediate',
    interval: 1,
    maxExecutions: 1,
    timeOfDay: '09:00',
    dayOfWeek: 1,
    messageTemplate: ''
  });

  // Field options for the dropdown
  const fieldOptions = [
    { value: 'rsvp', label: 'RSVP Status', values: ['yes', 'no', 'maybe'] },
    { value: 'plusOnes', label: 'Plus Ones', values: ['0', '1', '2', '3+'] },
    { value: 'idProvided', label: 'ID Provided', values: ['', 'pending'] },
    { value: 'roomAssigned', label: 'Room Assigned', values: ['yes', 'no', 'maybe'] }
  ];

  // Helper function to calculate next execution date
  const calculateNextExecutionDate = () => {
    const now = new Date();
    let nextDate;
    
    if (formData.frequency === 'immediate') {
      nextDate = new Date(now.getTime() + 60 * 1000); // 1 minute from now
    } else {
      const [hours, minutes] = formData.timeOfDay.split(':').map(Number);
      
      if (formData.frequency === 'daily') {
        nextDate = new Date(now);
        nextDate.setDate(nextDate.getDate() + formData.interval);
        nextDate.setHours(hours, minutes, 0, 0);
        
        // If the time has passed today, schedule for tomorrow
        if (nextDate <= now) {
          nextDate.setDate(nextDate.getDate() + 1);
        }
      } else if (formData.frequency === 'weekly') {
        nextDate = new Date(now);
        const currentDay = now.getDay() === 0 ? 7 : now.getDay(); // Convert Sunday to 7
        const targetDay = formData.dayOfWeek;
        const daysToAdd = targetDay >= currentDay ? 
          targetDay - currentDay : 
          7 - currentDay + targetDay;
        
        nextDate.setDate(nextDate.getDate() + daysToAdd);
        nextDate.setHours(hours, minutes, 0, 0);
        
        if (nextDate <= now) {
          nextDate.setDate(nextDate.getDate() + 7);
        }
      }
    }
    
    return nextDate;
  };

  // Helper function to get user-friendly trigger labels
  const getTriggerLabel = (field) => {
    const labels = {
      rsvp: 'RSVP is',
      plusOnes: 'Plus ones is',
      idProvided: 'ID status is',
      roomAssigned: 'Room status is'
    };
    return labels[field] || 'Value is';
  };

  // Helper function to format trigger value display
  const formatTriggerValue = (field, value) => {
    if (field === 'idProvided' && value === '') return 'Not provided';
    if (field === 'idProvided' && value === 'pending') return 'Pending';
    if (field === 'plusOnes') return value === '0' ? 'None (0)' : value;
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const frequencyOptions = [
    { value: 'immediate', label: 'Immediate (1 minute - for testing)' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' }
  ];

  // Default message templates
  const defaultTemplates = {
    rsvp: {
      no: {
        oneTime: `Thank you for informing us - we received your RSVP as "no" for {eventName}. We'll miss you!`,
        recurring: `Hi {guestName}, we haven't received your RSVP for {eventName} on {eventDate}. Please reply with your response!`
      },
      maybe: {
        oneTime: `Hi {guestName}, we see you're undecided about {eventName}. Please confirm your attendance by replying with Yes or No.`,
        recurring: `Hi {guestName}, please update us with your RSVP status for {eventName}. We need to finalize headcount.`
      }
    },
    plusOnes: {
      '0': {
        oneTime: `Hi {guestName}, we noticed you haven't indicated if you'll bring a plus one to {eventName}. Please let us know!`,
        recurring: `Hi {guestName}, we still need to know about plus ones for {eventName}. Please confirm.`
      }
    }
  };

  useEffect(() => {
    fetchEventData();
  }, [eventId]);

  useEffect(() => {
    // Initialize form data if editing existing rule
    if (editingRule) {
      setFormData({
        name: editingRule.name,
        triggerField: editingRule.triggerField,
        triggerValue: editingRule.triggerValue,
        actionType: editingRule.actionType || 'whatsapp_message',
        isRecurring: editingRule.isRecurring || false,
        frequency: editingRule.schedule?.frequency || 'immediate',
        interval: editingRule.schedule?.interval || 1,
        maxExecutions: editingRule.schedule?.maxExecutions || 1,
        timeOfDay: editingRule.schedule?.timeOfDay || '09:00',
        dayOfWeek: editingRule.schedule?.dayOfWeek || 1,
        messageTemplate: editingRule.messageTemplate || ''
      });
    }
  }, [editingRule]);

  useEffect(() => {
    // Update message template when trigger field/value or recurring status changes
    if (!editingRule) { // Only auto-update template for new rules
      updateMessageTemplate();
    }
  }, [formData.triggerField, formData.triggerValue, formData.isRecurring, editingRule]);

  const fetchEventData = async () => {
    try {
      const eventData = await eventService.getEvent(eventId);
      setEvent(eventData);
      setGuests(eventData.guests || []);
    } catch (error) {
      console.error('Error fetching event data:', error);
      setError('Failed to load event data');
    } finally {
      setLoading(false);
      // Short delay to ensure smooth transition
      setTimeout(() => setInitialLoad(false), 100);
    }
  };

  const updateMessageTemplate = () => {
    const template = defaultTemplates[formData.triggerField]?.[formData.triggerValue];
    if (template) {
      const messageType = formData.isRecurring ? 'recurring' : 'oneTime';
      const newTemplate = template[messageType] || template.oneTime || '';
      setFormData(prev => ({ ...prev, messageTemplate: newTemplate }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    // Form validation
    if (!formData.name.trim()) {
      setError('Alert rule name is required');
      setSaving(false);
      return;
    }

    if (!formData.messageTemplate.trim()) {
      setError('Message template is required');
      setSaving(false);
      return;
    }

    try {
      const ruleData = {
        name: formData.name,
        triggerField: formData.triggerField,
        triggerValue: formData.triggerValue,
        actionType: formData.actionType,
        isRecurring: formData.isRecurring,
        schedule: {
          frequency: formData.frequency,
          interval: formData.interval,
          maxExecutions: formData.maxExecutions,
          timeOfDay: formData.timeOfDay,
          dayOfWeek: formData.dayOfWeek
        },
        messageTemplate: formData.messageTemplate
      };

      if (editingRule) {
        // Update existing rule
        await alertRuleService.updateRule(editingRule._id || editingRule.id, ruleData);
      } else {
        // Create new rule
        await alertRuleService.createRule(eventId, ruleData);
      }
      
      // Navigate back to the event details page with alerts tab active
      navigate(`/event/${eventId}`, { state: { activeTab: 'alerts' }, replace: true });
    } catch (error) {
      console.error('Error saving alert rule:', error);
      setError(`Failed to ${editingRule ? 'update' : 'create'} alert rule. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const renderFormField = (field, label, icon, component) => {
    return (
      <div className="form-field">
        <label className="form-label">
          {icon}
          <span>{label}</span>
        </label>
        <div className="form-input-container">
          {component}
        </div>
      </div>
    );
  };

  const renderSelectField = (field, label, icon, options, value) => {
    return renderFormField(field, label, icon, (
      <select
        value={value}
        onChange={(e) => handleInputChange(field, e.target.value)}
        className="form-input"
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    ));
  };

  const renderTextAreaField = (field, label, icon, placeholder = '') => {
    return renderFormField(field, label, icon, (
      <textarea
        value={formData[field]}
        onChange={(e) => handleInputChange(field, e.target.value)}
        placeholder={placeholder}
        className="form-input"
        rows={4}
        style={{ resize: 'vertical', fontFamily: 'inherit' }}
      />
    ));
  };

  if (loading) {
    return (
      <div className="event-overview-container">
        <div className="overview-header">
          <div className="header-left">
            <button 
              className="back-button"
              onClick={() => navigate(`/event/${eventId}`, { state: { activeTab: 'alerts' }, replace: true })}
              title="Back to Event Details"
            >
              <ArrowBackIcon />
            </button>
            <div>
              <h3>{editingRule ? 'Edit Alert Rule' : 'New Alert Rule'}</h3>
              <p className="overview-subtitle">Loading...</p>
            </div>
          </div>
        </div>
        <div className="event-form">
          <div className="loading-spinner-container" style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            flexDirection: 'column'
          }}>
            <div className="loading-spinner" style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #1e514b',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '16px', color: '#666' }}>Loading event data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-overview-container">
        <div className="error-message">Event not found</div>
      </div>
    );
  }

  const selectedField = fieldOptions.find(opt => opt.value === formData.triggerField);

  return (
    <div className={`event-overview-container ${initialLoad ? 'page-transition-enter' : 'page-transition-enter-active'}`}>
      <div className="overview-header">
        <div className="header-left">
          <button 
            className="back-button"
            onClick={() => navigate(`/event/${eventId}`, { state: { activeTab: 'alerts' }, replace: true })}
            title="Back to Event Details"
          >
            <ArrowBackIcon />
          </button>
          <div>
            <h3>{editingRule ? 'Edit Alert Rule' : 'New Alert Rule'}</h3>
            <p className="overview-subtitle">For {event.name}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="event-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {renderFormField(
          'name',
          'Rule name',
          <EditIcon className="form-icon" />,
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="e.g., RSVP Follow-up"
            className="form-input"
            required
          />
        )}

        {renderSelectField(
          'triggerField',
          'Condition',
          <TriggerIcon className="form-icon" />,
          fieldOptions,
          formData.triggerField
        )}

        {renderSelectField(
          'triggerValue',
          getTriggerLabel(formData.triggerField),
          <TriggerIcon className="form-icon" />,
          selectedField?.values?.map(val => ({ value: val, label: formatTriggerValue(formData.triggerField, val) })) || [],
          formData.triggerValue
        )}

        {renderFormField(
          'isRecurring',
          'Recurring',
          <RepeatIcon className="form-icon" />,
          <select
            value={formData.isRecurring}
            onChange={(e) => handleInputChange('isRecurring', e.target.value === 'true')}
            className="form-input"
          >
            <option value="false">One-time</option>
            <option value="true">Recurring</option>
          </select>
        )}

        {renderSelectField(
          'frequency',
          'Frequency',
          <ScheduleIcon className="form-icon" />,
          frequencyOptions,
          formData.frequency
        )}

        {renderFormField(
          'maxExecutions',
          '# attempts',
          <ScheduleIcon className="form-icon" />,
          <input
            type="number"
            value={formData.maxExecutions}
            onChange={(e) => handleInputChange('maxExecutions', parseInt(e.target.value))}
            min="1"
            className="form-input"
          />
        )}

        {/* Upcoming Schedule Preview */}
        {formData.frequency !== 'immediate' && (
          <div className="schedule-preview-section">
            <h4>
              <ScheduleIcon style={{ fontSize: '16px' }} />
              Upcoming Schedule Preview
            </h4>
            <div className="schedule-preview">
              <div className="preview-item">
                <span className="preview-label">Next execution:</span>
                <span className="preview-value">
                  {calculateNextExecutionDate().toLocaleString('en-US', {
                    weekday: 'short',
                    month: 'short', 
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              {formData.isRecurring && formData.maxExecutions > 1 && (
                <div className="preview-item">
                  <span className="preview-label">Second execution:</span>
                  <span className="preview-value">
                    {(() => {
                      const firstDate = calculateNextExecutionDate();
                      const secondDate = new Date(firstDate);
                      if (formData.frequency === 'daily') {
                        secondDate.setDate(secondDate.getDate() + formData.interval);
                      } else if (formData.frequency === 'weekly') {
                        secondDate.setDate(secondDate.getDate() + 7 * formData.interval);
                      }
                      return secondDate.toLocaleString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric', 
                        hour: 'numeric',
                        minute: '2-digit'
                      });
                    })()
                    }
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {renderTextAreaField(
          'messageTemplate',
          'Message template',
          <MessageIcon className="form-icon" />,
          'Enter your message template here...'
        )}

        {/* Template Variables Help Section */}
        <div className="template-variables-section">
          <h4>
            <MessageIcon style={{ fontSize: '16px' }} />
            Available Template Variables
          </h4>
          <div className="variable-grid">
            <div className="variable-item">
              <code className="variable-code">
                {'{guestName}'}
              </code>
              <span className="variable-description">Guest's name</span>
            </div>
            <div className="variable-item">
              <code className="variable-code">
                {'{eventName}'}
              </code>
              <span className="variable-description">Event name</span>
            </div>
            <div className="variable-item">
              <code className="variable-code">
                {'{eventDate}'}
              </code>
              <span className="variable-description">Event date</span>
            </div>
          </div>
          <p className="help-text">
            These variables will be automatically replaced with actual values when messages are sent.
          </p>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="save-event-btn"
            disabled={saving}
          >
{saving ? (editingRule ? 'Updating...' : 'Creating...') : (editingRule ? 'Update alert rule' : 'Save alert rule')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAlertRulePage;